/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Link } from "../components/ui/Link";
import { backHref } from "../lib/navigation";
import { Check } from "lucide-react";
import {
  confirmRegistryMerge,
  dismissRegistryCase,
  getRegistryCase,
  REGISTRY_SIGNAL_LABELS,
  type RegistryCaseDetail,
  type RegistryCaseSide,
} from "../api/registryDuplicates";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { CopyableId } from "../components/ui/CopyableId";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import { Textarea } from "../components/ui/Textarea";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { canEditRegistry } from "../components/RegistryBits";
import { cn } from "../lib/utils";

/**
 * One suspected registry pair, side by side. The reviewer keeps one record; merging re-points
 * every referral, provider membership, and fax reference to it and retires the other record.
 * Dismissing silences the pair permanently. Differing fields are highlighted.
 */
export function ReferralDuplicateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = useAuth();
  const { showToast } = useToast();

  const [item, setItem] = useState<RegistryCaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [survivor, setSurvivor] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState<"merge" | "dismiss" | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id === undefined) {
      return;
    }
    void getRegistryCase(id).then((response) => {
      if (response.ok) {
        setItem(response.value);
        setError(null);
      } else {
        setError(response.error.message);
      }
    });
  }, [id]);

  useChrome(
    { title: "Duplicate review", back: { to: "/referrals/duplicates", label: "Duplicates" } },
    [],
  );

  if (error !== null) {
    return <ErrorMessage message={error} className="mt-8" />;
  }
  if (item === null || id === undefined) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const provider = item.entityType === "PROVIDER";
  const canAct = canEditRegistry(hasRole) && item.status === "OPEN";
  const loser =
    survivor === null ? null : survivor === item.entityA.id ? item.entityB : item.entityA;

  async function resolve(kind: "merge" | "dismiss") {
    if (id === undefined) {
      return;
    }
    setBusy(true);
    const response =
      kind === "merge" && survivor !== null
        ? await confirmRegistryMerge(id, survivor, note.trim())
        : await dismissRegistryCase(id, note.trim());
    setBusy(false);
    setConfirming(null);
    if (response.ok) {
      showToast(
        "success",
        kind === "merge" ? "Records merged." : "Dismissed. This pair will not resurface.",
      );
      void navigate(backHref("/referrals/duplicates", location.state));
    } else {
      showToast("error", response.error.message);
    }
  }

  return (
    <div className="max-w-5xl space-y-6 pb-4">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          {provider ? "Are these the same provider?" : "Are these the same organization?"}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Badge tone="info">Score {item.score}</Badge>
          {item.signals.map((signal) => (
            <span key={signal} className="text-xs text-text-tertiary">
              {REGISTRY_SIGNAL_LABELS[signal] ?? signal}
            </span>
          ))}
          {item.status !== "OPEN" && (
            <Badge tone={item.status === "MERGED" ? "success" : "neutral"}>
              {item.status === "MERGED" ? `Merged into ${item.survivorId}` : "Dismissed"}
            </Badge>
          )}
        </div>
      </header>

      <div className="enter grid gap-4 md:grid-cols-2">
        {[
          [item.entityA, item.entityB],
          [item.entityB, item.entityA],
        ].map(([side, other]) => (
          <SideCard
            key={side!.id}
            side={side!}
            other={other!}
            provider={provider}
            selected={survivor === side!.id}
            selectable={canAct}
            onKeep={() => setSurvivor(side!.id)}
          />
        ))}
      </div>

      {canAct && (
        <div className="enter space-y-3">
          <Textarea
            id="resolution-note"
            label="Resolution note"
            placeholder="What you verified and why"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="md"
              disabled={busy || note.trim() === ""}
              onClick={() => setConfirming("dismiss")}
            >
              {provider ? "Not the same provider" : "Not the same organization"}
            </Button>
            <Button
              size="md"
              disabled={busy || survivor === null || note.trim() === ""}
              onClick={() => setConfirming("merge")}
            >
              Merge into kept record
            </Button>
          </div>
        </div>
      )}

      <Modal
        isOpen={confirming === "merge"}
        onClose={() => setConfirming(null)}
        title={provider ? "Merge providers" : "Merge organizations"}
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            {loser !== null && (
              <>
                <span className="font-medium text-text-primary">{loser.name}</span> (record{" "}
                {loser.id}) will be retired.{" "}
                {provider
                  ? "Its referrals, care contacts, and fax references move to the kept provider."
                  : "Its providers, referrals, care contacts, and fax references move to the kept organization."}{" "}
                This cannot be undone from the console.
              </>
            )}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void resolve("merge")}>
              {busy ? <Spinner size="xs" className="mr-2" /> : null}
              Merge
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirming === "dismiss"}
        onClose={() => setConfirming(null)}
        title="Dismiss this pair"
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            These records will never be flagged as duplicates of each other again.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void resolve("dismiss")}>
              Dismiss
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function SideCard({
  side,
  other,
  provider,
  selected,
  selectable,
  onKeep,
}: {
  side: RegistryCaseSide;
  other: RegistryCaseSide;
  provider: boolean;
  selected: boolean;
  selectable: boolean;
  onKeep: () => void;
}) {
  const differs = (pick: (s: RegistryCaseSide) => string | null) =>
    (pick(side) ?? "") !== (pick(other) ?? "");
  return (
    <section
      className={cn(
        "space-y-4 rounded-3xl border bg-bg-elevated p-5 shadow-card transition-colors",
        selected ? "border-accent/50 bg-accent/5" : "border-border",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate font-display text-lg leading-tight text-text-primary">
            <Link
              className="hover:underline"
              to={`/referrals/${provider ? "providers" : "organizations"}/${side.id}`}
            >
              {side.name ?? `Record ${side.id}`}
            </Link>
          </h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {side.deleted && <Badge tone="neutral">Retired</Badge>}
            <CopyableId label="Record" value={String(side.id)} />
          </div>
        </div>
        {selectable && (
          <Button
            variant={selected ? "primary" : "outline"}
            size="sm"
            aria-pressed={selected}
            onClick={onKeep}
          >
            {selected && <Check size={14} className="mr-1" />}
            {selected ? "Keeping" : "Keep"}
          </Button>
        )}
      </div>

      <dl className="space-y-1.5 text-sm">
        {provider ? (
          <>
            <Fact label="Email" highlight={differs((s) => s.email)}>
              {side.email ?? "—"}
            </Fact>
            <Fact label="Fax" highlight={differs((s) => s.fax)}>
              {side.fax ?? "—"}
            </Fact>
            <Fact label="Phone" highlight={differs((s) => s.phone)}>
              {side.phone ?? "—"}
            </Fact>
            <Fact label="Organization" highlight={differs((s) => s.organization)}>
              {side.organization ?? "—"}
            </Fact>
          </>
        ) : (
          <>
            <Fact label="State" highlight={differs((s) => s.state)}>
              {side.state ?? "—"}
            </Fact>
            <Fact label="Email domain" highlight={differs((s) => s.domain)}>
              {side.domain ?? "—"}
            </Fact>
          </>
        )}
        <Fact label="Created" highlight={false}>
          {side.createdAt
            ? new Date(side.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "—"}
        </Fact>
      </dl>
    </section>
  );
}

function Fact({
  label,
  highlight,
  children,
}: {
  label: string;
  highlight: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-text-tertiary">{label}</dt>
      <dd className={cn("truncate", highlight ? "font-medium text-warning" : "text-text-secondary")}>
        {children}
      </dd>
    </div>
  );
}
